// src/MinistryManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext'; // (1. Import useAuth - ถูกต้อง)
import toast from 'react-hot-toast';

const API_URL = 'https://rys-backend.onrender.com/api';

// (2. ไม่มี getAuthHeaders ตัวเก่า)

function MinistryManagement() { // (3. ชื่อฟังก์ชันถูกต้อง)
  const navigate = useNavigate();
  // (4. ดึง getAuthHeaders "ตัวใหม่" มาจาก useAuth)
  const { user, getAuthHeaders } = useAuth();

  const [ministries, setMinistries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [ministryName, setMinistryName] = useState('');
  const [editingId, setEditingId] = useState(null);

  // States สำหรับ Modal ลบ
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);

  // (5. fetchData ที่ถูกต้องสำหรับ /api/ministries)
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/ministries`, getAuthHeaders());
      setMinistries(res.data);
    } catch (err) {
      toast.error("ไม่สามารถโหลดข้อมูลกระทรวงได้");
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders]); // (6. เพิ่ม getAuthHeaders ใน dependency)

  useEffect(() => {
    fetchData();
  }, [fetchData]); 

  const clearForm = () => {
    setMinistryName('');
    setEditingId(null);
  };

  // (7. handleSubmit ที่ถูกต้องสำหรับ /api/ministries)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ministryName) return toast.error("กรุณากรอกชื่อกระทรวง");
    
    const payload = { name: ministryName };
    const method = editingId ? 'put' : 'post';
    const url = editingId 
      ? `${API_URL}/ministries/${editingId}` 
      : `${API_URL}/ministries`;

    try {
      const res = await axios({ method, url, data: payload, ...getAuthHeaders() });
      toast.success(res.data.msg);
      clearForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'ดำเนินการไม่สำเร็จ');
    }
  };

  const handleToggleActive = async (item) => {
    try {
      // (ยิง API ใหม่)
      const res = await axios.put(`${API_URL}/ministries/${item.id}/toggle`, {}, getAuthHeaders());
      toast.success(res.data.msg);
      fetchData(); // (รีเฟรชตาราง)
    } catch (err) {
      toast.error("เปลี่ยนสถานะไม่สำเร็จ");
    }
  };

  const handleEditClick = (item) => {
    setEditingId(item.id);
    setMinistryName(item.name);
  };

  const handleOpenDeleteModal = (item) => {
    setDeletingItem(item);
    setIsDeleteModalOpen(true);
  };

  // (8. handleConfirmDelete ที่ถูกต้องสำหรับ /api/ministries)
  const handleConfirmDelete = async () => {
    if (!deletingItem) return;
    try {
      const res = await axios.delete(`${API_URL}/ministries/${deletingItem.id}`, getAuthHeaders());
      toast.success(res.data.msg);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'ลบไม่สำเร็จ');
    } finally {
      setIsDeleteModalOpen(false);
      setDeletingItem(null);
    }
  };
  
  if (user?.role !== 'admin') {
    navigate('/');
    return null;
  }

  return (
    <>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8">
        <button
          onClick={() => navigate('/admin/feeds')}
          className="mb-6 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          &larr; ย้อนกลับ
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* (ฟอร์ม Ministry) */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-xl p-8 sticky top-28">
              <h2 className="text-2xl font-semibold text-gray-800 mb-6">
                {editingId ? 'แก้ไขกระทรวง' : 'เพิ่มกระทรวงใหม่'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="ministryName" className="block text-base font-medium text-gray-700">ชื่อกระทรวง *</label>
                  <input
                    type="text" id="ministryName" value={ministryName}
                    onChange={(e) => setMinistryName(e.target.value)}
                    required
                    className="mt-1 block w-full px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm"
                  />
                </div>
                <button
                  type="submit"
                  className={`w-full px-4 py-3 font-semibold text-xl text-white rounded-md ${editingId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {editingId ? 'บันทึกการแก้ไข' : 'เพิ่มกระทรวง'}
                </button>
                {editingId && (
                  <button type="button" onClick={clearForm} className="w-full mt-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-md">
                    ยกเลิก
                  </button>
                )}
              </form>
            </div>
          </div>
          {/* (ตาราง Ministry) */}
          <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-xl">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4 border-b pb-2">กระทรวงที่มีอยู่</h3>
            {isLoading ? <p>Loading...</p> : (
              <table className="min-w-full divide-y divide-gray-200">
                
                {/* --- ▼▼▼ (แก้ไข 1: <thead>) ▼▼▼ --- */}
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left ... w-20">ลำดับ</th>
                    <th className="px-6 py-3 text-left ...">ชื่อกระทรวง</th>
                    <th className="px-6 py-3 text-left ...">สถานะ (หน้า Feed)</th>
                    <th className="px-6 py-3 text-right ...">ดำเนินการ</th>
                  </tr>
                </thead>
                
                <tbody className="bg-white divide-y divide-gray-200">
                  {ministries.map((item, index) => (
                    <tr key={item.id} className={!item.is_active ? 'bg-gray-100 opacity-60' : ''}>
                      <td className="px-6 py-4 text-lg">{index + 1}</td>
                      <td className="px-6 py-4 text-lg font-medium">{item.name}</td>
                      
                      {/* (คอลัมน์สถานะ) */}
                      <td className="px-6 py-4 text-lg">
                        {item.is_active ? (
                          <span className="text-green-600 font-semibold">แสดง</span>
                        ) : (
                          <span className="text-gray-500">ซ่อน</span>
                        )}
                      </td>                        
                        <td className="px-6 py-4 text-right space-x-4">
                        {/* (ปุ่ม ซ่อน/แสดง) */}
                        <button 
                          onClick={() => handleToggleActive(item)}
                          className={item.is_active ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}
                        >
                          {item.is_active ? 'ซ่อน' : 'แสดง'}
                        </button>
                        <button onClick={() => handleEditClick(item)} className="text-blue-600 hover:text-blue-900">แก้ไข</button>
                        <button onClick={() => handleOpenDeleteModal(item)} className="text-red-600 hover:text-red-900">ลบ</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* (Modal ลบ Ministry) */}
      {isDeleteModalOpen && deletingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-xl m-4">
            <h2 className="text-2xl font-semibold mb-5 text-gray-800 border-b pb-3">ยืนยันการลบกระทรวง</h2>
            <p className="mb-6 text-lg">
              คุณแน่ใจหรือไม่ว่าต้องการลบ: <strong className="text-red-600">{deletingItem.name}</strong>?
            </p>
            <p className="text-base text-gray-600">
              (หมายเหตุ: จะลบได้ต่อเมื่อไม่มี "หน่วยงาน" ผูกอยู่)
            </p>
            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                ยืนยันการลบ
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MinistryManagement;